import { apiClient } from '@/api/client.js';
import { forceFileDownloadLink } from '@/api/attachmentsApi.js';

/**
 * @typedef {Object} FileLibraryItem
 * @property {number} id
 * @property {string} name
 * @property {'file' | 'folder'} type
 * @property {number} parent_id
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} GetItemsParams
 * @property {string} [query] - Строка поиска
 * @property {number} [development_center_id] - ID центра разработки
 * @property {number} [parent_id] - ID родителя
 * @property {string[]} [types] - Типы элементов
 * @property {string} [sort_field] - Поле сортировки
 * @property {string} [sort_direction] - Направление сортировки
 * @property {string|number} [page]
 */

/**
 * @typedef {Object} CreateItemData
 * @property {string} name
 * @property {'file' | 'folder'} type
 * @property {number} parent_id
 * @property {File} [file] - Файл для загрузки
 */

// API методы
export const fileLibraryApi = {
    /**
     * Получить элементы библиотеки файлов
     * @param {GetItemsParams} params
     * @param {import('axios').AxiosRequestConfig} [config]
     * @returns {Promise<{success: boolean, data: FileLibraryItem[]}>}
     */
    getItems: async (params = {}, config) => {
        const response = await apiClient.get('/file-library/v1/manage/items', {
            ...config,
            params,
        });

        return response.data;
    },

    itemServe: itemId => (itemId ? `/content/api/file-library/v1/manage/items/${itemId}/serve` : null),

    /**
     * Создать новый элемент в библиотеке файлов
     * @param {CreateItemData} data
     * @param {import('axios').AxiosRequestConfig} [config]
     * @returns {Promise<{success: boolean, data: FileLibraryItem}>}
     */
    createItem: async (data, config) => {
        const formData = new FormData();

        formData.append('name', data.name);
        formData.append('type', data.type);
        formData.append('parent_id', data.parent_id);

        if (data.file) {
            formData.append('file', data.file, data.file.name);
        }

        const response = await apiClient.post('/file-library/v1/manage/items', formData, config);

        return response.data;
    },

    /**
     * Удалить элементы библиотеки файлов
     * @param {number[]} items - Массив ID элементов для удаления
     * @returns {Promise<{success: boolean}>}
     */
    deleteItems: async items => {
        const response = await apiClient.delete('/file-library/v1/manage/items', {
            data: { items },
        });

        return response.data;
    },

    /**
     * Переименовать элемент
     * @param {number} id - ID элемента
     * @param {string} name - Новое имя
     * @returns {Promise<{success: boolean, data: FileLibraryItem}>}
     */
    renameItem: async (id, name) => {
        const response = await apiClient.put(`/file-library/v1/manage/items/${id}`, {
            name,
        });

        return response.data;
    },

    /**
     * Копировать элементы
     * @param {number[]} items - Массив ID элементов
     * @param {number} parent_id - ID родителя
     * @returns {Promise<{success: boolean}>}
     */
    copyItems: async (items, parent_id) => {
        const response = await apiClient.post('/file-library/v1/manage/items/copy', {
            items,
            parent_id,
        });

        return response.data;
    },

    /**
     * Переместить элементы
     * @param {number[]} items - Массив ID элементов
     * @param {number} parent_id - ID родителя
     * @returns {Promise<{success: boolean}>}
     */
    moveItems: async (items, parent_id) => {
        const response = await apiClient.post('/file-library/v1/manage/items/move', {
            items,
            parent_id,
        });

        return response.data;
    },

    /**
     * Получить элементы избранного
     * @param {GetItemsParams} params
     * @param {import('axios').AxiosRequestConfig} [config]
     * @returns {Promise<{success: boolean, data: FileLibraryItem[]}>}
     */
    getFavoriteItems: async (params = {}, config) => {
        const response = await apiClient.get('/file-library/v1/manage/items/favorite', {
            ...config,
            params,
        });

        return response.data;
    },

    /**
     * Добавить элементы в избранное
     * @param {number[]} items - Массив ID элементов
     * @returns {Promise<{success: boolean}>}
     */
    addToFavorites: async items => {
        const response = await apiClient.post('/file-library/v1/manage/items/favorite', {
            items,
        });

        return response.data;
    },

    /**
     * Удалить элементы из избранного
     * @param {number[]} items - Массив ID элементов
     * @returns {Promise<{success: boolean}>}
     */
    removeFromFavorites: async items => {
        const response = await apiClient.delete('/file-library/v1/manage/items/favorite', {
            data: { items },
        });

        return response.data;
    },

    /**
     * @param {Array<string|number>} itemIds
     */
    downloadItems: itemIds => {
        if (!itemIds?.length) return;

        const url = new URL('/content/api/file-library/v1/manage/items/download', window.location.origin);

        url.searchParams.append('only_images', '1');

        itemIds.forEach(id => {
            url.searchParams.append('items[]', String(id));
        });

        const downloadUrl = url.toString();

        forceFileDownloadLink(downloadUrl);
    },

    /**
     * @param {{ id: string|number, type: 'project'|'course'|'attachment' }} entity
     * @returns {Promise<*>}
     */
    getLibItemByEntity: async entity => {
        const { data: resData } = await apiClient.post('/file-library/v1/manage/item-by-entity', {
            entity_id: entity.id,
            entity_type: entity.type,
        });

        return resData.data;
    },

    /**
     * @param itemId
     * @returns {Promise<Array<*>>}
     */
    getLibItemPath: async (itemId, config) => {
        const { data: respData } = await apiClient.get(`/file-library/v1/manage/items/${itemId}/path`, config);

        return respData.data;
    },

    getLibIdForCourse(courseId) {
        return this.getLibItemByEntity({
            id: courseId,
            type: 'course',
        });
    },

    getLibIdForProject(projectId) {
        return this.getLibItemByEntity({
            id: projectId,
            type: 'project',
        });
    },
};
